<template>
  <!-- Skeleton Loader -->
  <div v-if="loading" class="slider-container skeleton">
    <div class="slider-wrapper">
      <div class="skeleton-slide">
        <div class="skeleton-image"></div>
        <div class="skeleton-overlay">
          <div class="skeleton-category"></div>
          <div class="skeleton-title"></div>
          <div class="skeleton-excerpt"></div>
        </div>
      </div>
      <div class="slider-dots skeleton-dots">
        <div class="skeleton-dot"></div>
        <div class="skeleton-dot"></div>
        <div class="skeleton-dot"></div>
      </div>
    </div>
  </div>

  <!-- Slider real -->
  <div class="slider-container" v-else-if="posts.length > 0">
    <div class="slider-wrapper">
      <div 
        class="slider-track"
        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div 
          v-for="post in posts" 
          :key="post.id"
          class="slide"
          @click="goToPost(post)"
        >
          <img :src="getImageUrl(post.image)" :alt="post.title" class="slide-image">
          <div class="slide-overlay">
            <div class="slide-content">
              <span class="slide-category">{{ post.category?.name || 'Noticia' }}</span>
              <h3 class="slide-title">{{ post.title }}</h3>
              <p class="slide-excerpt">{{ post.extract }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Indicadores -->
      <div class="slider-dots" v-if="posts.length > 1">
        <button
          v-for="(_, idx) in posts"
          :key="idx"
          class="dot"
          :class="{ active: currentIndex === idx }"
          @click="goToSlide(idx)"
        ></button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../services/api'

const router = useRouter()
const posts = ref([])
const currentIndex = ref(0)
const loading = ref(true)
let autoPlayInterval = null
let touchStartX = 0
let touchEndX = 0

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const loadFeaturedPosts = async () => {
  loading.value = true
  try {
    const response = await api.get('/featured')
    posts.value = response.data.posts || []
  } catch (error) {
    console.error('Error cargando slider:', error)
    posts.value = []
  } finally {
    loading.value = false
  }
}

const nextSlide = () => {
  if (posts.value.length === 0) return
  currentIndex.value = (currentIndex.value + 1) % posts.value.length
}

const prevSlide = () => {
  if (posts.value.length === 0) return
  currentIndex.value = (currentIndex.value - 1 + posts.value.length) % posts.value.length
}

const goToSlide = (index) => {
  currentIndex.value = index
  resetAutoPlay()
}

const goToPost = (post) => {
  router.push(`/noticia/${post.slug}`)
}

const startAutoPlay = () => {
  if (autoPlayInterval) clearInterval(autoPlayInterval)
  autoPlayInterval = setInterval(() => {
    nextSlide()
  }, 5000)
}

const resetAutoPlay = () => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
    startAutoPlay()
  }
}

// Swipe con dedos
const onTouchStart = (e) => {
  touchStartX = e.touches[0].clientX
}

const onTouchMove = (e) => {
  touchEndX = e.touches[0].clientX
}

const onTouchEnd = () => {
  const difference = touchStartX - touchEndX
  const minSwipeDistance = 50
  
  if (Math.abs(difference) > minSwipeDistance) {
    if (difference > 0) {
      nextSlide()
    } else {
      prevSlide()
    }
  }
  
  resetAutoPlay()
  touchStartX = 0
  touchEndX = 0
}

onMounted(() => {
  loadFeaturedPosts()
  startAutoPlay()
})

onUnmounted(() => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
  }
})
</script>

<style scoped>
.slider-container {
  width: 100%;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.slider-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

.slider-track {
  display: flex;
  transition: transform 0.5s ease-in-out;
  cursor: grab;
}

.slider-track:active {
  cursor: grabbing;
}

.slide {
  flex: 0 0 100%;
  position: relative;
  cursor: pointer;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.slide-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.85), transparent);
  padding: 30px 20px 20px;
}

.slide-content {
  color: white;
  margin-bottom: 20px;
}

.slide-category {
  display: inline-block;
  background: #a50000;
  color: white;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
  text-transform: uppercase;
  font-weight: bold;
}

.slide-title {
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.slide-excerpt {
  font-size: 13px;
  margin: 0;
  opacity: 0.9;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

/* Indicadores */
.slider-dots {
  position: absolute;
  bottom: 15px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 10;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.5);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
}

.dot.active {
  width: 30px;
  border-radius: 4px;
  background: #a50000;
}

/* Skeleton Loader Styles */
.skeleton-slide {
  aspect-ratio: 16 / 9;
  position: relative;
  overflow: hidden;
}

.skeleton-image {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px 20px 20px;
}

.skeleton-category {
  width: 80px;
  height: 24px;
  background: linear-gradient(90deg, #c0c0c0 25%, #d0d0d0 50%, #c0c0c0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 20px;
  margin-bottom: 10px;
}

.skeleton-title {
  width: 90%;
  height: 22px;
  background: linear-gradient(90deg, #c0c0c0 25%, #d0d0d0 50%, #c0c0c0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-excerpt {
  width: 70%;
  height: 16px;
  background: linear-gradient(90deg, #c0c0c0 25%, #d0d0d0 50%, #c0c0c0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-dots {
  bottom: 15px;
}

.skeleton-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0,0,0,0.2);
  display: inline-block;
  margin: 0 5px;
}

.skeleton-dots {
  display: flex;
  justify-content: center;
  gap: 10px;
  position: absolute;
  bottom: 15px;
  left: 0;
  right: 0;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .slide-title {
    font-size: 16px;
  }
  
  .slide-excerpt {
    font-size: 12px;
  }
  
  .slide-overlay {
    padding: 25px 15px 15px;
  }
}

@media (max-width: 480px) {
  .slide-title {
    font-size: 14px;
  }
  
  .slide-excerpt {
    font-size: 11px;
    -webkit-line-clamp: 1;
  }
  
  .slide-overlay {
    padding: 20px 12px 12px;
  }
  
  .dot.active {
    width: 24px;
  }
}
</style>